function _extends() { _extends = Object.assign ? Object.assign.bind() : function (target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i]; for (var key in source) { if (Object.prototype.hasOwnProperty.call(source, key)) { target[key] = source[key]; } } } return target; }; return _extends.apply(this, arguments); }
import React, { useState, useEffect, useCallback, useRef } from 'react';
import styled, { css } from 'styled-components';
import { ErrorOutline } from '@styled-icons/material-outlined/ErrorOutline';
import { CheckCircleOutline } from '@styled-icons/material-outlined/CheckCircleOutline';
import { Close } from '@styled-icons/material/Close';
import { Text, TextVariant } from '@naf/text';
import { spacing, radius, themeLight } from '@naf/theme';
import { propValidationConfig } from "./propValidationConfig";
export let ToastVariant = /*#__PURE__*/function (ToastVariant) {
  ToastVariant["Information"] = "information";
  ToastVariant["Success"] = "success";
  ToastVariant["Error"] = "error";
  return ToastVariant;
}({});
export const useToast = () => {
  const [visible, setVisible] = useState(false);
  const [variant, setVariant] = useState(ToastVariant.Information);
  const [icon, setIcon] = useState(false);
  const [action, setAction] = useState();
  const [closable, setClosable] = useState(false);
  const [timeOut, setTimeout] = useState(5000);
  const [message, setMessage] = useState();
  const Icon = ({
    iconVariant
  }) => {
    if (iconVariant === 'success') {
      return /*#__PURE__*/React.createElement(CheckCircleOutline, {
        size: 24
      });
    }
    return /*#__PURE__*/React.createElement(ErrorOutline, {
      size: 24
    });
  };
  const closeToast = () => {
    setVisible(false);
  };
  const showToast = useCallback(({
    variant,
    icon,
    action,
    closable,
    timeOut,
    message
  }) => {
    setVisible(true);
    if (variant) {
      setVariant(variant);
    } else setVariant(ToastVariant.Information);
    if (icon) {
      setIcon(icon);
    } else setIcon(false);
    if (action) {
      setAction(action);
    } else setAction(undefined);
    if (closable) {
      setClosable(closable);
    } else setClosable(false);
    if (timeOut) {
      setTimeout(timeOut);
    } else if (timeOut === false) {
      setTimeout(false);
    }
    if (message) {
      setMessage(message);
    }
  }, [setVisible, setVariant, setIcon, setAction, setClosable, setTimeout, setMessage]);
  const Toast = ({
    className,
    ...rest
  }) => {
    const toastRef = useRef(null);
    const handleClickOutside = e => {
      if (!!toastRef.current && !toastRef.current.contains(e.target)) {
        setVisible(false);
      }
    };
    useEffect(() => {
      window.addEventListener('mousedown', e => {
        handleClickOutside(e);
      });
      return () => {
        window.removeEventListener('mousedown', e => {
          handleClickOutside(e);
        });
      };
    }, []);
    useEffect(() => {
      if (typeof timeOut === 'number' && !closable && action === undefined) {
        const interval = setInterval(() => {
          setVisible(false);
        }, timeOut);
        return () => {
          clearInterval(interval);
        };
      }
      return () => {};
    }, []);
    return /*#__PURE__*/React.createElement(Container, _extends({
      ref: toastRef,
      "aria-hidden": visible,
      "aria-label": "Varsling",
      className: className,
      variant: variant,
      visible: visible
    }, rest), !action && !closable && icon && /*#__PURE__*/React.createElement(ToastIcon, null, /*#__PURE__*/React.createElement(Icon, {
      iconVariant: variant
    })), /*#__PURE__*/React.createElement(ToastText, {
      variant: TextVariant.ArticleText
    }, message), action !== undefined && /*#__PURE__*/React.createElement(ActionButton, {
      onClick: action.onClick
    }, /*#__PURE__*/React.createElement(ButtonText, {
      variant: TextVariant.ArticleText
    }, action.text)), action === undefined && closable && /*#__PURE__*/React.createElement(CloseButton, {
      onClick: () => setVisible(false)
    }, /*#__PURE__*/React.createElement(Close, {
      size: 24
    })));
  };
  return {
    showToast,
    Toast,
    closeToast
  };
};
const CenterSvg = css(["svg{display:flex;align-items:center;}"]);
const ResetButton = css(["border:none;margin:0;padding:0;width:auto;overflow:visible;background:transparent;color:inherit;font:inherit;line-height:normal;appearance:none;touch-action:manipulation;"]);
const Container = styled.div.withConfig(propValidationConfig).withConfig({
  displayName: "toast__Container",
  componentId: "sc-153u5iz-0"
})(["display:", ";color:", ";align-items:center;width:fit-content;position:fixed;bottom:", ";left:0;right:0;margin:0 auto;border-radius:", ";padding:", " ", ";z-index:9999;background-color:", ";", " ", " ", ""], props => props.visible ? 'flex' : 'none', ({
  theme
}) => theme.typography ? theme.typography.defaultTextInverted : themeLight.typography.defaultTextInverted, spacing.space24, radius.s, spacing.space12, spacing.space16, ({
  theme
}) => theme.componentColors ? theme.componentColors.cta.secondaryInverted : themeLight.componentColors.cta.secondaryInverted, ({
  variant
}) => variant === 'information' && css(["background-color:", ";"], ({
  theme
}) => theme.componentColors ? theme.componentColors.cta.secondaryInverted : themeLight.componentColors.cta.secondaryInverted), ({
  variant
}) => variant === 'success' && css(["background-color:", ";"], ({
  theme
}) => theme.componentColors ? theme.componentColors.alert.success : themeLight.componentColors.alert.success), ({
  variant
}) => variant === 'error' && css(["background-color:", ";"], ({
  theme
}) => theme.componentColors ? theme.componentColors.alert.error : themeLight.componentColors.alert.error));
const ToastText = styled(Text).withConfig({
  displayName: "toast__ToastText",
  componentId: "sc-153u5iz-1"
})(["display:inline-flex;margin:0;"]);
const ToastIcon = styled.div.withConfig({
  displayName: "toast__ToastIcon",
  componentId: "sc-153u5iz-2"
})(["display:inline-flex;", " svg{margin-right:", ";}"], CenterSvg, spacing.space12);
const ActionButton = styled.button.withConfig({
  displayName: "toast__ActionButton",
  componentId: "sc-153u5iz-3"
})(["", ";margin-left:", ";cursor:pointer;"], ResetButton, spacing.space24);
const ButtonText = styled(Text).withConfig({
  displayName: "toast__ButtonText",
  componentId: "sc-153u5iz-4"
})(["margin:0;text-decoration:underline;"]);
const CloseButton = styled.button.withConfig({
  displayName: "toast__CloseButton",
  componentId: "sc-153u5iz-5"
})(["", ";cursor:pointer;margin-left:", ";", ""], ResetButton, spacing.space16, CenterSvg);
//# sourceMappingURL=toast.js.map